import React, { useCallback } from 'react';
import Button from '../../../components/Button/CTA/index.js';
import LinkLocale from '../../../components/Route/Link/index.js';
import BoatGallery from '../Gallery/index.js';
import BoatSidebar from '../Sidebar/index.js';
import BoatMessages from '../Messages/index.js';
import BoatLayout from '../LayoutDetails/index.js';
import BoatCalendar from '../Calendar/index.js';
import CancellationContent from '../../Vacation/Cancellation/Content/index.js';
import DepositOptions from '../Deposit/index.js';
import EquimpentList from '../Equipment/index.js';
import ExtraList from '../Extras/index.js';
import CruiseLocation from '../Location/index.js';
import { useTranslation } from '../../../hooks/locale/translate.js';
import { useLocalizedUrl } from '../../../hooks/locale/url.js';
import { useLazyEffect } from '../../../hooks/effects/lazy.js';
import { useToggle } from '../../../hooks/effects/toggle.js';
import { useCruise } from '../../../hooks/boat/cruise.js';
import { getCustomEquipments, getEquipment, hasAttachments, getAttachments } from '../../../utils/parsers/boat/cruise.js';
import { getCancellationPercentage } from '../../../utils/parsers/bundle.js';
import { logDebug } from '../../../utils/debug/logger.js';
import { pushToDataLayer } from '../../../utils/tracking/gtm/index.js';
import { boatEcommerceEvent, boatEvent } from '../../../utils/tracking/gtm/events/boat.js';
import styles from './styles.scss.js';
import { hasCompulsoryExtras, hasOptionalExtras, hasBundleOptions, getBundleOptions, hasTripDates, getCompulsoryExtras, getOptionalExtras } from '../../../utils/parsers/boat/listed.js';

const BoatPreview = ({ skipper, onOpenBoatEnquiry, onOpenBookingFlow, }) => {
    const t = useTranslation();
    const localizedUrl = useLocalizedUrl();
    const [cruise, loadCruise] = useCruise();
    const handleOpenBoatEnquiry = useCallback(() => {
        if (typeof onOpenBoatEnquiry === 'function')
            onOpenBoatEnquiry(cruise);
    }, [cruise, onOpenBoatEnquiry]);
    const handleOpenBookingFlow = useCallback(() => {
        window.history.replaceState({
            url: `/cruise/${cruise.id}/book`,
        }, null, localizedUrl('/cruise/:id/book', { id: cruise.id }));
        onOpenBookingFlow(cruise);
    }, [cruise, onOpenBookingFlow]);
    const [showExtras, toggleExtras] = useToggle();
    const hasMoreExtras = !!getCustomEquipments(cruise)
        || hasCompulsoryExtras(cruise) || hasOptionalExtras(cruise);
    const higherBundle = !hasBundleOptions(cruise) ? null
        : getBundleOptions(cruise)[getBundleOptions(cruise).length - 1];
    useLazyEffect(() => loadCruise(cruise.id), []);
    useLazyEffect(() => {
        if (hasTripDates(cruise) && !window.location.href.match(`/${cruise.id}`)) {
            logDebug('BoatPreview: update cruise URL', cruise.id);
            window.history.replaceState({
                url: `/cruise/${cruise.id}`,
            }, null, localizedUrl('/cruise/:id', { id: cruise.id }));
            pushToDataLayer(boatEcommerceEvent(cruise, 'Cruise Page'));
            pushToDataLayer(boatEvent(cruise));
        }
    }, [cruise], false);
    return (React.createElement("div", null,
        React.createElement(BoatGallery, null),
        React.createElement("div", { className: styles.container },
            React.createElement("div", { className: styles.main },
                React.createElement(BoatSidebar, { skipper: skipper, openBoatEnquiry: handleOpenBoatEnquiry, openBookingFlow: handleOpenBookingFlow })),
            React.createElement("div", { className: styles.aside },
                React.createElement(BoatMessages, { className: styles.textFieldset }),
                React.createElement(BoatLayout, { className: styles.layoutFieldset }),
                React.createElement("div", { className: styles.fieldset },
                    React.createElement("h5", { className: styles.fieldsetTitle }, t('Equipment')),
                    React.createElement(EquimpentList, { equipment: getEquipment(cruise), otherEquipment: showExtras && getCustomEquipments(cruise) }),
                    (hasMoreExtras && !showExtras) && (React.createElement(Button, { className: styles.link, onClick: toggleExtras, asLink: true }, t('Show more')))),
                React.createElement("div", { className: styles.extrasFieldset },
                    (showExtras && hasCompulsoryExtras(cruise)) && (React.createElement("div", null,
                        React.createElement("h5", { className: styles.fieldsetTitle }, t('Compulsory extras')),
                        React.createElement(ExtraList, { list: getCompulsoryExtras(cruise), compulsory: true }))),
                    (showExtras && hasOptionalExtras(cruise)) && (React.createElement("div", null,
                        React.createElement("h5", { className: styles.fieldsetTitle }, t('Available extras')),
                        React.createElement("p", { className: styles.fieldsetCaption }, t('Optional extras that you can customize after reservation')),
                        React.createElement(ExtraList, { list: getOptionalExtras(cruise) }))))),
            higherBundle && (React.createElement("div", { className: styles.fieldset },
                React.createElement("h3", { className: styles.fieldsetTitle }, t('Cancellation protection')),
                React.createElement("p", { className: styles.fieldsetCaption }, t('Optional extras that you can customize after reservation')),
                React.createElement(CancellationContent, { percentage: getCancellationPercentage(higherBundle) }))),
            React.createElement("div", { className: styles.fieldset },
                React.createElement("h3", { className: styles.fieldsetTitle }, t('Security deposit')),
                React.createElement("p", { className: styles.fieldsetCaption }, t('All our boats are part of fleets of professional charter companies, therefore they all are insured. The deposit represents the insurance excess of the boat itself. This covers any accidental damage caused by you or your fellow passengers and aims to ensure that all the contractual obligations are met (e.g. at the checkout when you return the boat to the marina). It is given to the boat manager before setting sail and is returned at the end of the trip after the boat has been inspected and if there is no damage.')),
                React.createElement(DepositOptions, null)),
            React.createElement("div", { className: styles.calendarFieldset },
                React.createElement("h3", { className: styles.fieldsetTitle }, t('Calendar')),
                React.createElement(BoatCalendar, null)),
            React.createElement("div", { className: styles.fieldset },
                React.createElement("h3", { className: styles.fieldsetTitle }, t('Location')),
                React.createElement(CruiseLocation, null)),
            hasAttachments(cruise) && (React.createElement("div", { className: styles.fieldset },
                React.createElement("h3", { className: styles.fieldsetTitle }, t('Additional documents')),
                React.createElement("div", { className: styles.attachments }, getAttachments(cruise).map((file) => (React.createElement(LinkLocale, { key: file.url, href: file.url, title: t('Download'), rel: "nofollow noopener" }, file.title)))))))));
};

export { BoatPreview as default };
//# sourceMappingURL=index.js.map
